import base64url from 'base64-url';
import moment from 'moment';
import {SymbolicDate} from './enums';

/**
 * Sestaví URL do mapio.
 *
 * @param {string} serverUrl - URL serveru
 * @param {object} [options] - volitelné parametry
 * @param {string} [options.path] - cesta ke stránce v URL
 * @param {object} [options.queryParams] - query parametry v URL
 * @param {object} [options.contextParams] - kontextové parametry pro aplikační konektory
 * @param {string} [options.instanceId] - unikátní ID instance mapio
 * @param {string} [options.topic] - téma konfigurace
 * @param {string} [options.backgroundLayer] - ID podkladové vrstvy
 * @param {string} [options.foregroundLayer] - ID popisné vrstvy
 * @param {Array<string>} [options.layers] - pole ID viditelných datových vrstev
 * @param {Array<string>} [options.catalogNodes] - pole ID otevřených uzlů katalogu
 * @param {Array<number>} [options.location] - pole 2 souřadnic [longitude, latitude] udávající střed mapy
 * @param {string} [options.zoom] - zoom mapy
 * @param {Object} [options.filter] - filtr pro features v datových vrstvách
 * @param {Date|string} [options.filter.date] - specifické datum, nebo symbolická datumová konstanta
 * @param {Date} [options.filter.dateFrom] - počáteční datum (včetně)
 * @param {Date} [options.filter.dateTo] - koncové datum (včetně)
 * @returns {string} výsledná URL
 */
export function buildMapioUrl(serverUrl, options = {}) {
    const {
        path,
        queryParams = {},
        contextParams,
        instanceId,
        topic,
        backgroundLayer,
        foregroundLayer,
        layers,
        catalogNodes,
        location,
        zoom,
        filter,
    } = options;

    let url = serverUrl;

    if (url[url.length - 1] === '/') {
        url = url.substring(0, url.length - 1);
    }

    if (path) {
        if (path[0] !== '/') {
            url += '/';
        }

        url += path;

        if (url[url.length - 1] === '/') {
            url = url.substring(0, url.length - 1);
        }
    }

    if (contextParams && Object.keys(contextParams).length) {
        queryParams.context = base64url.encode(serializeParams(contextParams));
    }

    if (instanceId != null) {
        queryParams.instanceId = instanceId;
    }

    if (topic != null) {
        queryParams.topic = topic;
    }

    if (backgroundLayer != null) {
        queryParams.bgLayer = backgroundLayer;
    }

    if (foregroundLayer != null) {
        queryParams.fgLayer = foregroundLayer;
    }

    if (layers && layers.length) {
        queryParams.layers = layers.join(',');
    }

    if (catalogNodes && catalogNodes.length) {
        queryParams.catalogNodes = catalogNodes.join(',');
    }

    if (location && location[0] != null && location[1] != null) {
        queryParams.lon = location[0];
        queryParams.lat = location[1];
    }

    if (zoom != null) {
        queryParams.zoom = zoom;
    }

    if (filter != null) {
        const date = getDateQueryValue(filter);
        if (date) {
            queryParams.date = date;
        }
    }

    if (Object.keys(queryParams).length) {
        url += '?' + serializeParams(queryParams);
    }

    return url;
}

// Konstanta udávající neomezený rozsah v datumovém filtru
const unlimitedDateQueryValue = '*';

// Převod na symbolické datumové konstanty použivé v query URL
const symbolicDateQueryValues = {
    [SymbolicDate.TODAY]: 'd',
    [SymbolicDate.TODAY_PLUS_WEEK]: 'd+w',
    [SymbolicDate.TODAY_PLUS_TWO_WEEKS]: 'd+2w',
    [SymbolicDate.TODAY_PLUS_MONTH]: 'd+m',
    [SymbolicDate.TODAY_AND_FUTURE]: 'd+f',
    [SymbolicDate.TODAY_PLUS_MINUS_WEEK]: 'd+w-w',
    [SymbolicDate.THIS_WEEK]: 'w',
    [SymbolicDate.THIS_MONTH]: 'm',
    [SymbolicDate.THIS_YEAR]: 'y',
    [SymbolicDate.NEXT_WEEK]: 'nw',
    [SymbolicDate.NEXT_MONTH]: 'nm',
    [SymbolicDate.NEXT_YEAR]: 'ny',
};

/**
 * Vrátí hodnotu datumového filtru ve formě řetězce použitelného v URL.
 *
 * @param {Object} filter - datový filtr pro features ve vrstvách
 * @returns {string} výsledný řetězec
 */
function getDateQueryValue(filter) {
    const {date} = filter;
    if (date !== undefined) {
        if (date instanceof Date) {
            return formatDateQueryValue(date);
        }
        return symbolicDateQueryValues[date] || unlimitedDateQueryValue;
    }
    const {dateFrom, dateTo} = filter;
    if (dateFrom !== undefined || dateTo !== undefined) {
        if (dateFrom || dateTo) {
            return formatDateQueryValue(dateFrom) + ',' + formatDateQueryValue(dateTo);
        }
        return unlimitedDateQueryValue;
    }
    return null; // V query se nic nenastaví, použije se výchozí hodnota v mapio
}

/**
 * Naformátuje datum pro použití v URL.
 *
 * @param {Date} date - datum
 * @returns {string} výsledný řetězec
 */
function formatDateQueryValue(date) {
    return date instanceof Date ? moment(date).format('YYYY-MM-DD') : '';
}

/**
 * Serializuje parametry do query string v URL.
 *
 * @param {Object} params - parametry
 * @returns {string} výsledný řetězec
 */
function serializeParams(params) {
    return Object.keys(params)
        .sort() // Potřebujeme aby to bylo deterministické kvůli testům
        .filter(key => params[key] != null)
        .map(key => `${key}=${params[key]}`)
        .join('&');
}

/**
 * Vytvoří kopii feature s přidanými properties.
 *
 * @param {Object} feature - feature
 * @param {Array<string>} properties - properties pro přidání
 * @returns {Object} kopie feature s novými properties, nebo pouze původní
 *                   feature pokud nedošlo k přidání žádných properties
 */
export function addFeatureProperties(feature, properties) {
    if (feature.properties) {
        return {...feature, properties: {...feature.properties, ...properties}};
    }
    return {...feature, properties};
}

/**
 * Vytvoří kopii feature s odebranými properties.
 *
 * @param {Object} feature - feature
 * @param {Array<string>} properties -  názvy properties pro odebrání
 * @returns {Object} nová feature
 */
export function removeFeatureProperties(feature, names) {
    const {properties, ...newFeature} = feature;
    const newProperties = properties ? {...properties} : {};

    for (const name of names) {
        delete newProperties[name];
    }

    if (Object.keys(newProperties).length) {
        // Nechceme zbytečně vracet prázdné properties, aby se zjednodušila případná serializovaná forma
        newFeature.properties = newProperties;
    }

    return newFeature;
}
